import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { applicationsApi } from 'api/endpoints/applications.api';
import {
  Application,
  ApplicationCreateRequest,
  ApplicationUpdateRequest,
  AppUser,
  AppUserFilter,
  AppUserStats,
  ApplicationCopyRequest,
  ApplicationLookup
} from 'types/application.types';

interface ApplicationsState {
  applications: Application[];
  applicationsLookup: ApplicationLookup[];
  currentApplication: Application | null;
  appUsers: AppUser[];
  appUsersTotal: number;
  appUserStats: AppUserStats | null;
  total: number;
  isLoading: boolean;
  isCurrentLoading: boolean;
  isUsersLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  applicationsRequestId: string | null;
  appUsersRequestId: string | null;
}

const initialState: ApplicationsState = {
  applications: [],
  applicationsLookup: [],
  currentApplication: null,
  appUsers: [],
  appUsersTotal: 0,
  appUserStats: null,
  total: 0,
  isLoading: false,
  isCurrentLoading: false,
  isUsersLoading: false,
  isSubmitting: false,
  error: null,
  applicationsRequestId: null,
  appUsersRequestId: null,
};

export const fetchApplications = createAsyncThunk(
  'applications/fetchAll',
  async ({ limit = 20, offset = 0, search }: { limit?: number; offset?: number; search?: string }) => {
    const response = await applicationsApi.list(limit, offset, search);
    return response;
  }
);

export const fetchApplicationsLookup = createAsyncThunk(
  'applications/fetchLookup',
  async () => {
    const response = await applicationsApi.lookup();
    return response.applications;
  }
);

export const fetchApplication = createAsyncThunk(
  'applications/fetchOne',
  async (id: number) => {
    const response = await applicationsApi.get(id);
    return response;
  }
);

export const createApplication = createAsyncThunk(
  'applications/create',
  async (data: ApplicationCreateRequest) => {
    const response = await applicationsApi.create(data);
    return response;
  }
);

export const updateApplication = createAsyncThunk(
  'applications/update',
  async ({ id, data }: { id: number; data: ApplicationUpdateRequest }) => {
    const response = await applicationsApi.update(id, data);
    return response;
  }
);

export const copyApplication = createAsyncThunk(
  'applications/copy',
  async ({ id, data }: { id: number; data: ApplicationCopyRequest }) => {
    const response = await applicationsApi.copy(id, data);
    return response;
  }
);

export const deleteApplication = createAsyncThunk(
  'applications/delete',
  async (id: number) => {
    await applicationsApi.delete(id);
    return id;
  }
);

export const fetchAppUsers = createAsyncThunk(
  'applications/fetchUsers',
  async ({ appId, filter }: { appId: number; filter?: AppUserFilter }) => {
    const response = await applicationsApi.listUsers(appId, filter);
    return response;
  }
);

export const fetchAppUserStats = createAsyncThunk(
  'applications/fetchUserStats',
  async (appId: number) => {
    const response = await applicationsApi.getUserStats(appId);
    return response;
  }
);

export const deleteAppUser = createAsyncThunk(
  'applications/deleteUser',
  async ({ appId, userId, reason }: { appId: number; userId: number; reason?: string }) => {
    await applicationsApi.deleteUser(appId, userId, reason);
    return userId;
  }
);

export const restoreAppUser = createAsyncThunk(
  'applications/restoreUser',
  async ({ appId, userId }: { appId: number; userId: number }) => {
    await applicationsApi.restoreUser(appId, userId);
    return userId;
  }
);

export const exportAppUsers = createAsyncThunk(
  'applications/exportUsers',
  async ({ appId, filter }: { appId: number; filter?: AppUserFilter }) => {
    const response = await applicationsApi.exportUsers(appId, filter);
    return response;
  }
);

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    setCurrentApplication: (state, action: PayloadAction<Application | null>) => {
      state.currentApplication = action.payload;
    },
    clearApplications: (state) => {
      state.applications = [];
      state.total = 0;
      state.applicationsRequestId = null;
    },
    clearAppUsers: (state) => {
      state.appUsers = [];
      state.appUsersTotal = 0;
      state.appUsersRequestId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApplications.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
      state.applicationsRequestId = action.meta.requestId;
    });
    builder.addCase(fetchApplications.fulfilled, (state, action) => {
      if (state.applicationsRequestId !== action.meta.requestId) return;
      state.isLoading = false;
      state.applicationsRequestId = null;
      state.applications = action.payload.applications || [];
      state.total = action.payload.total || 0;
    });
    builder.addCase(fetchApplications.rejected, (state, action) => {
      if (state.applicationsRequestId !== action.meta.requestId) return;
      state.isLoading = false;
      state.applicationsRequestId = null;
      state.error = action.error.message || 'Failed to fetch applications';
    });

    builder.addCase(fetchApplicationsLookup.fulfilled, (state, action) => {
      state.applicationsLookup = action.payload;
    });
    builder.addCase(fetchApplicationsLookup.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch applications lookup';
    });

    builder.addCase(fetchApplication.pending, (state) => {
      state.isCurrentLoading = true;
      state.error = null;
    });
    builder.addCase(fetchApplication.fulfilled, (state, action) => {
      state.isCurrentLoading = false;
      state.currentApplication = action.payload;
    });
    builder.addCase(fetchApplication.rejected, (state, action) => {
      state.isCurrentLoading = false;
      state.error = action.error.message || 'Failed to fetch application';
    });

    builder.addCase(createApplication.pending, (state) => {
      state.isSubmitting = true;
      state.error = null;
    });
    builder.addCase(createApplication.fulfilled, (state) => {
      state.isSubmitting = false;
    });
    builder.addCase(createApplication.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.error.message || 'Failed to create application';
    });

    builder.addCase(updateApplication.pending, (state) => {
      state.isSubmitting = true;
      state.error = null;
    });
    builder.addCase(updateApplication.fulfilled, (state, action) => {
      state.isSubmitting = false;
      const index = state.applications.findIndex(app => app.id === action.payload.application.id);
      if (index !== -1) {
        state.applications[index] = action.payload.application;
      }
      if (state.currentApplication?.id === action.payload.application.id) {
        state.currentApplication = action.payload.application;
      }
    });
    builder.addCase(updateApplication.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.error.message || 'Failed to update application';
    });

    builder.addCase(copyApplication.pending, (state) => {
      state.isSubmitting = true;
      state.error = null;
    });
    builder.addCase(copyApplication.fulfilled, (state) => {
      state.isSubmitting = false;
    });
    builder.addCase(copyApplication.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.error.message || 'Failed to copy application';
    });

    builder.addCase(deleteApplication.pending, (state) => {
      state.isSubmitting = true;
      state.error = null;
    });
    builder.addCase(deleteApplication.fulfilled, (state, action) => {
      state.isSubmitting = false;
      state.applications = state.applications.filter(app => app.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
      if (state.currentApplication?.id === action.payload) {
        state.currentApplication = null;
      }
    });
    builder.addCase(deleteApplication.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = action.error.message || 'Failed to delete application';
    });

    builder.addCase(fetchAppUsers.pending, (state, action) => {
      state.isUsersLoading = true;
      state.error = null;
      state.appUsersRequestId = action.meta.requestId;
    });
    builder.addCase(fetchAppUsers.fulfilled, (state, action) => {
      if (state.appUsersRequestId !== action.meta.requestId) return;
      state.isUsersLoading = false;
      state.appUsersRequestId = null;
      state.appUsers = action.payload.users || [];
      state.appUsersTotal = action.payload.total || 0;
    });
    builder.addCase(fetchAppUsers.rejected, (state, action) => {
      if (state.appUsersRequestId !== action.meta.requestId) return;
      state.isUsersLoading = false;
      state.appUsersRequestId = null;
      state.error = action.error.message || 'Failed to fetch users';
    });

    builder.addCase(fetchAppUserStats.fulfilled, (state, action) => {
      state.appUserStats = action.payload;
    });

    builder.addCase(deleteAppUser.fulfilled, (state, action) => {
      const user = state.appUsers.find(u => u.id === action.payload);
      if (user) {
        user.is_active = false;
      }
    });

    builder.addCase(restoreAppUser.fulfilled, (state, action) => {
      const user = state.appUsers.find(u => u.id === action.payload);
      if (user) {
        user.is_active = true;
      }
    });
  },
});

export const {
  setCurrentApplication,
  clearApplications,
  clearAppUsers,
  clearError,
} = applicationsSlice.actions;

export const selectApplications = (state: { applications: ApplicationsState }) =>
  state.applications.applications;
export const selectApplicationsLookup = (state: { applications: ApplicationsState }) =>
  state.applications.applicationsLookup;
export const selectCurrentApplication = (state: { applications: ApplicationsState }) =>
  state.applications.currentApplication;
export const selectAppUsers = (state: { applications: ApplicationsState }) =>
  state.applications.appUsers;
export const selectAppUsersTotal = (state: { applications: ApplicationsState }) =>
  state.applications.appUsersTotal;
export const selectAppUserStats = (state: { applications: ApplicationsState }) =>
  state.applications.appUserStats;
export const selectApplicationsTotal = (state: { applications: ApplicationsState }) =>
  state.applications.total;
export const selectApplicationsLoading = (state: { applications: ApplicationsState }) =>
  state.applications.isLoading;
export const selectCurrentApplicationLoading = (state: { applications: ApplicationsState }) =>
  state.applications.isCurrentLoading;
export const selectAppUsersLoading = (state: { applications: ApplicationsState }) =>
  state.applications.isUsersLoading;
export const selectApplicationsSubmitting = (state: { applications: ApplicationsState }) =>
  state.applications.isSubmitting;
export const selectApplicationsError = (state: { applications: ApplicationsState }) =>
  state.applications.error;

export default applicationsSlice.reducer;